'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bot, ChevronRight, Clock, Download, GitFork, Mail, Pencil, Pin, PinOff, Trash2, Users } from 'lucide-react';
import { projectName } from 'elowen-plugin-shared/projectChoice';
import { interpolate, useTranslation } from '../../lib/i18n';
import { findNormalizedRange } from '../../lib/normalizeText';
import { brainModelQualifiedLabel } from '../../lib/modelProvider';
import { formatTaskTime, formatTokens } from '../../lib/format';
import { cacheHitPct, formatCost, formatSpeed } from '../../lib/displayFormat';
import { ActionMenu, type ActionMenuItem } from '../../components/ui/ActionMenu';
import { ContextMenu, ContextMenuTrigger, toMenuEntries, type ContextMenuState } from '../../components/ui/ContextMenu';
import { Avatar } from '../../components/ui/Avatar';
import { AvatarStack } from '../../components/ui/AvatarStack';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/shadcn/popover';
import { ConversationMembersPopover } from './ConversationMembersPopover';
import { Tooltip, TooltipAnchor, TooltipContent } from '../../components/ui/shadcn/tooltip';
import { useTooltipAnchor } from '../../components/ui/shadcn/useTooltipAnchor';
import { StatusMark } from '../../components/ui/StatusMark';
import { Button } from '../../components/ui/Button';
import { ConversationIcon } from '../../components/ui/ConversationIcon';
import { ConversationIconPicker } from '../../components/ui/ConversationIconPicker';
import { MarkStrip } from '../../components/ui/MarkStrip';
import { AVATAR_STACK_RING, AVATAR_STACK_SIZE } from '../../components/ui/avatarStack';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { PlatformIcon } from '../../components/ui/PlatformIcon';
import { ProjectIcon } from '../../components/ui/ProjectIcon';
import { Checkbox } from '../../components/ui/shadcn/checkbox';
import { Input } from '../../components/ui/shadcn/input';
import { Item, ItemActions, ItemContent, ItemMedia } from '../../components/ui/shadcn/item';
import { profileAvatarUser, type ConversationListRowProps } from './conversationListModel';

/** Faces a row's member stack shows before the rest fold into a count (SPEC 1.3). */
const ROW_FACES = 3;

function Highlight({ text, search }: { text: string; search: string }) {
  const range = findNormalizedRange(text, search);
  if (!range) return <>{text}</>;
  return <>{text.slice(0, range[0])}<mark className="rounded-sm bg-primary/30 px-0.5 text-foreground">
    {text.slice(range[0], range[1])}
  </mark>{text.slice(range[1])}</>;
}

/** A row's visual record; ConversationList supplies the enclosing semantic item and nested branches. */
export function ConversationListRow(props: ConversationListRowProps) {
  const { t, locale } = useTranslation();
  const { item, actions, busy } = props;
  const agentsTip = useTooltipAnchor();
  const scheduledTip = useTooltipAnchor();
  const title = item.title || t.brainChat.untitled;
  const [editing, setEditing] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameStatus, setRenameStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const editorPhase = useRef<'idle' | 'editing' | 'saving'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef<HTMLButtonElement>(null);
  const restoreOpenFocus = useRef(false);
  const renameCallback = useRef(props.onRenameChange);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const live = useRef(true);
  useEffect(() => { renameCallback.current = props.onRenameChange; }, [props.onRenameChange]);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      if (editorPhase.current !== 'idle') renameCallback.current?.(false);
    };
  }, []);
  useEffect(() => {
    if (editing && renameStatus !== 'saving') inputRef.current?.focus();
  }, [editing, renameStatus]);
  useLayoutEffect(() => {
    if (!editing && restoreOpenFocus.current) {
      restoreOpenFocus.current = false;
      openRef.current?.focus();
    }
  }, [editing]);

  const canSelect = props.selectable && !props.contextOnly && !busy && !editing;
  const disabled = busy || editing;
  const finishRename = (restoreFocus = false) => {
    restoreOpenFocus.current = restoreFocus;
    editorPhase.current = 'idle';
    setEditing(false);
    setRenameStatus('idle');
    renameCallback.current?.(false);
  };
  const startRename = () => {
    editorPhase.current = 'editing';
    setRenameValue(item.title);
    setRenameStatus('idle');
    setEditing(true);
    renameCallback.current?.(true);
  };
  const commitRename = async (restoreFocus = false) => {
    // Enter can disable/unmount the field and cause blur in the same event sequence.
    if (editorPhase.current !== 'editing') return;
    const value = renameValue.trim();
    if (!value) { setRenameStatus('error'); return; }
    if (value === item.title) { finishRename(restoreFocus); return; }
    editorPhase.current = 'saving';
    setRenameStatus('saving');
    const saved = await actions.rename(item, value);
    if (!live.current) return;
    if (saved) finishRename(restoreFocus);
    else {
      editorPhase.current = 'editing';
      setRenameStatus('error');
    }
  };
  if (item.kind === 'conversation' && (item.isOwner || item.capabilities.leave) && !item.notifications) {
    throw new Error('Conversation member is missing notification preferences');
  }
  const menuItems: ActionMenuItem[] = [
    ...(props.selectable && !props.contextOnly ? [{
      action: 'select' as const, disabled, onSelect: props.onSelect,
    }] : []),
    ...(item.capabilities.pin ? [{
      action: item.pinnedAt ? 'unpin' as const : 'pin' as const,
      disabled, onSelect: () => actions.pin(item),
    }] : []),
    ...(item.capabilities.rename ? [{
      action: 'rename' as const, disabled, onSelect: () => {}, onAfterClose: startRename,
    }] : []),
    ...((item.isOwner || item.capabilities.leave) && item.notifications ? [{
      action: item.notifications.muted ? 'unmute' as const : 'mute' as const,
      label: item.notifications.muted ? t.brainChat.ux.unmuteConversation : t.brainChat.ux.muteConversation,
      disabled: disabled || actions.mutePending(item.id), onSelect: () => {},
      onAfterClose: () => actions.mute(item),
    }] : []),
    ...((item.capabilities.invite || item.capabilities.leave) ? [{
      label: t.chat.participants, icon: Users, disabled, onSelect: () => {}, onAfterClose: () => setMembersOpen(true),
    }] : []),
    ...(item.isOwner && item.capabilities.archive ? [{
      action: item.archivedAt === null ? 'archive' as const : 'restore' as const,
      disabled: disabled || actions.archiveHeld(item.id), onSelect: () => {}, onAfterClose: () => actions.archive(item),
    }] : []),
    ...(item.capabilities.fork ? [{
      label: t.chat.fork, icon: GitFork, disabled, onSelect: () => actions.fork(item),
    }] : []),
    ...(item.capabilities.export ? [
      { action: 'download' as const, label: t.chat.exportHtml, disabled, onSelect: () => actions.export(item, 'html') },
      { action: 'download' as const, label: t.chat.exportJsonl, disabled, onSelect: () => actions.export(item, 'jsonl') },
    ] : []),
    ...(props.hasSubagents ? [{
      label: t.sessionsPanel.subAgents, icon: Bot, disabled, onSelect: () => actions.showSubagents(item),
    }] : []),
    ...(item.capabilities.leave ? [{
      action: 'leave' as const, disabled,
      onSelect: () => {}, onAfterClose: () => actions.leave(item),
    }] : []),
    ...(item.capabilities.delete ? [{
      action: 'delete' as const, disabled: disabled || props.deleteHeld,
      onSelect: () => {}, onAfterClose: () => actions.delete(item),
    }] : []),
  ];
  const state = item.programStatus?.state;
  const workingUsage = state === 'working' ? item.liveUsage : undefined;
  const cacheHit = cacheHitPct({ cacheRead: workingUsage?.cacheRead, cacheWrite: workingUsage?.cacheWrite, input: workingUsage?.input });
  const speed = formatSpeed(workingUsage?.effectiveTps, locale, 1);
  const stateText = state === 'blocked'
    ? !item.capabilities.continue ? t.conversationList.waitingForResponse
      : item.programStatus?.kind === 'permission' ? t.chat.activityPermission : t.chat.activityQuestion
    : state === 'error' ? [t.chat.activityFailed, item.activity?.detail].filter(Boolean).join(': ')
    : state === 'working' ? t.chat.activityWorking
    : item.backgroundWorking ? t.chat.activityBackground
    : state === 'done' ? item.activity?.automation === 'scheduled' ? t.chat.activityDoneScheduled : t.chat.activityDone
    : t.chat.activityIdle;
  const time = formatTaskTime(item.updated_at, props.now, locale);
  const projectLabel = item.project ? projectName(item.project) : item.projectIdentity?.name
    ?? (item.projectUnavailable ? t.conversationList.filters.unavailableProject : t.conversationList.filters.noProject);
  const shownProject = item.project ?? item.projectIdentity;
  const sharedRoom = item.kind === 'channel' && !item.direct;
  // A shared conversation names all its members where a row shows its owner, owner first (SPEC 1.3); an
  // unshared row shows its owner's face in All only.
  const members = item.participants.length > 0 ? [item.owner, ...item.participants] : null;
  const ownerFace = members === null && props.view === 'all' ? item.owner : null;
  const agentsLabel = props.runningAgents ? interpolate(props.runningAgents.truncated ? t.conversationList.runningAgentsAtLeast : t.conversationList.runningAgents, { count: props.runningAgents.count }) : '';
  const scheduledLabel = `${props.scheduledLabel}: ${props.scheduledCount}`;
  const branchLabel = `${title}: ${t.chat.historyTitle}`;
  const openLabel = `${item.capabilities.continue ? t.sessionsPanel.openInChat : t.sessionsPanel.viewInChat}: ${title}`;

  // The right-click / long-press menu holds the kebab's rows. Its actions run after it closed; one that
  // opens an editor or a dialog (rename, delete) first puts focus on the row's stable open control, so that
  // overlay returns focus there and not to wherever the pointer left it.
  const openMenu = (point: { x: number; y: number }) => setMenu({
    ...point,
    items: toMenuEntries(menuItems.filter(entry => entry.action !== 'archive' && entry.action !== 'restore').map((entry): ActionMenuItem => entry.onAfterClose ? {
      ...entry,
      onAfterClose: () => { openRef.current?.focus({ preventScroll: true }); entry.onAfterClose?.(); },
    } : entry)),
  });

  return <>
    <ContextMenuTrigger disabled={disabled} onOpen={openMenu}>
      <Item role="presentation" variant="selectable" size="sm"
        data-conversation-id={item.id} data-conversation-root={props.depth === 0 ? '' : undefined}
        data-selected={props.selected || undefined} aria-busy={renameStatus === 'saving' || undefined}
        className="group border-border bg-card hover:border-primary/40 data-[selected=true]:border-primary/60 data-[selected=true]:bg-selected-tint grid grid-cols-[var(--touch-target)_minmax(0,1fr)_var(--touch-target)] gap-x-2 gap-y-0.5 rounded-xl px-3 py-2 md:grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto_auto]"
        onClick={event => {
          if (!event.currentTarget.contains(event.target as Node) || busy || editing || (event.target as HTMLElement).closest('button, a, input, textarea, select, [role="menu"], [role="menuitem"], [role="checkbox"], [data-mark-strip="conversation-members"], [data-testid="conversation-members-popover"]')) return;
          actions.open(item);
        }}>
        <ItemMedia title={projectLabel} className="relative col-start-1 text-muted-foreground row-start-1 size-[var(--touch-target)] justify-self-center md:row-span-2 md:size-9">
          {props.selectionMode ? <Checkbox checked={props.selected} disabled={!canSelect} onCheckedChange={props.onSelect}
            className="pointer-coarse:size-[var(--touch-target)]" aria-label={interpolate(t.brainChat.selectRow, { title })} />
            : <ConversationIcon {...item} unread={item.unread === true} size={20}
              identity={shownProject ? <ProjectIcon project={shownProject} size={20} /> : undefined} />}
        </ItemMedia>
        <ItemContent className="col-start-2 row-start-1 gap-0.5 md:col-start-3">
          {editing ? <div className="flex min-w-0 flex-wrap items-center gap-1">
            <Input ref={inputRef} autoFocus data-keyboard-claimed aria-label={t.chat.renamePlaceholder}
              aria-invalid={renameStatus === 'error' && !renameValue.trim() || undefined}
              value={renameValue} disabled={renameStatus === 'saving'} onChange={event => setRenameValue(event.target.value)}
              onBlur={() => { void commitRename(); }}
              onKeyDown={event => {
                if (event.key === 'Enter') { event.preventDefault(); void commitRename(true); }
                if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finishRename(true); }
              }}
              className="h-8 min-w-0 flex-1" />
            {/* An empty name is marked invalid at the field itself (aria-invalid). A refused rename is reported
                once, by the list's toast, and this is only the way to try again. */}
            {renameStatus === 'error' && renameValue.trim()
              ? <Button type="button" variant="ghost" onClick={() => { void commitRename(); }}>{t.common.retry}</Button> : null}
          </div> : <div className="flex min-w-0 items-center gap-1.5">
            {/* Where the person writes, beside the name in a fixed box so names line up from row to row. */}
            {item.platform ? <span data-conversation-platform className="inline-flex size-4 shrink-0 items-center justify-center">
              <PlatformIcon platform={item.platform} size={14} />
            </span> : null}
            <Button ref={openRef} type="button" variant="ghost" disabled={busy} aria-label={openLabel} title={title}
              aria-current={props.active ? 'page' : undefined}
              className="h-auto min-w-0 flex-1 justify-start truncate rounded-sm px-0 text-left hover:bg-transparent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => actions.open(item)}
              onKeyDown={event => {
                if (event.key === ' ') { event.preventDefault(); if (canSelect) props.onSelect(); }
              }}>
              {/* The role sits on the label so the row's ink and its type can be stated independently:
                  an unread conversation takes the emphasis role, a read one the record name's. */}
              <span className={`min-w-0 truncate text-foreground ${item.unread ? 'text-emphasis' : 'text-card-title'}`}>
                <Highlight text={title} search={props.search} />
              </span>
            </Button>
          </div>}
        </ItemContent>
        <div data-conversation-secondary className="col-span-full row-start-2 flex min-w-0 items-center gap-1 md:contents">
          <div className="flex min-w-0 flex-1 items-center gap-2 truncate text-small text-muted-foreground md:col-start-3 md:row-start-2">
            <span data-conversation-detail className="min-w-0 truncate" title={props.snippet?.snippet ?? stateText}>
              {props.snippet ? <Highlight text={props.snippet.snippet} search={props.search} /> : stateText}
            </span>
            {props.contextOnly ? <span className="shrink-0">{t.conversationList.contextAncestor}</span> : null}
            {props.view === 'all' && item.ownerLabel ? <span className="truncate" title={item.ownerLabel}>
              {sharedRoom ? interpolate(t.conversationList.hostedBy, { name: item.ownerLabel }) : item.ownerLabel}
            </span> : null}
            {sharedRoom && item.lastWriterLabel ? <span className="truncate" title={item.lastWriterLabel}>
              {interpolate(t.conversationList.lastWriter, { name: item.lastWriterLabel })}
            </span> : null}
          </div>
          {props.hasBranch ? <Button type="button" variant="ghost" size="icon-sm" className="shrink-0 md:col-start-2 md:row-span-2 md:row-start-1"
            disabled={busy} onClick={props.onToggle} aria-label={branchLabel} aria-expanded={props.expanded}
            aria-controls={props.expanded ? props.branchId : undefined}>
            <ChevronRight size={14} aria-hidden className={props.expanded ? 'rotate-90' : ''} />
          </Button> : null}
          <div data-conversation-usage role="group" aria-label={t.conversationList.usage}
            className="hidden w-40 shrink-0 flex-col items-end gap-0.5 self-center whitespace-nowrap text-right text-small tabular-nums text-muted-foreground md:col-start-5 md:row-span-2 md:row-start-1 lg:flex">
            <div className="flex items-center gap-2">
              {item.tokens !== undefined ? <span title={t.stats.sessionTokens}>{formatTokens(item.tokens)} {t.sessionsPanel.tok}</span> : null}
              {item.cost !== undefined ? <span title={t.stats.cardTotalCost}>{formatCost(item.cost, 2)}</span> : null}
            </div>
            {speed !== '—' || cacheHit !== null ? <div className="flex items-center gap-2">
              {speed !== '—' ? <span title={t.stats.speed}>{speed}</span> : null}
              {cacheHit !== null ? <span title={t.stats.cacheHit}>{Math.round(cacheHit)}%</span> : null}
            </div> : null}
          </div>
          <ItemActions className="justify-end gap-1 md:col-start-6 md:row-span-2 md:row-start-1 md:w-56">
            <div className="relative hidden w-48 shrink-0 items-center justify-end md:flex">
              <time dateTime={item.updated_at} title={time.title} className="text-small tabular-nums text-muted-foreground group-hover:opacity-0 group-focus-within:opacity-0">{time.label}</time>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-end opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
                {item.capabilities.pin ? <Button type="button" variant="ghost" size="icon-sm" disabled={disabled} data-conversation-control="pin"
                  aria-label={item.pinnedAt ? t.chat.unpin : t.chat.pin} onClick={() => actions.pin(item)}>
                  {item.pinnedAt ? <PinOff size={14} aria-hidden /> : <Pin size={14} aria-hidden />}
                </Button> : null}
                {item.capabilities.rename ? <Button type="button" variant="ghost" size="icon-sm" disabled={disabled}
                  aria-label={t.chat.rename} onClick={startRename}><Pencil size={14} aria-hidden /></Button> : null}
                {item.capabilities.fork ? <Button type="button" variant="ghost" size="icon-sm" disabled={disabled}
                  aria-label={t.chat.fork} onClick={() => actions.fork(item)}><GitFork size={14} aria-hidden /></Button> : null}
                {item.capabilities.export ? <ActionMenu variant="kebab" size="sm" label={`${t.chat.exportHtml} / ${t.chat.exportJsonl}`} trigger={<Download size={14} aria-hidden />}
                  items={menuItems.filter(entry => entry.label === t.chat.exportHtml || entry.label === t.chat.exportJsonl)} /> : null}
                {item.capabilities.delete ? <Button type="button" variant="ghost-danger" size="icon-sm" disabled={disabled}
                  aria-label={t.brainChat.deleteChat} onClick={() => actions.delete(item)}><Trash2 size={14} aria-hidden /></Button> : null}
              </div>
            </div>
            {/* A row without the picker (another account's conversation in All) keeps its desktop slot, so the
                time ends on the same right edge in every row. */}
            {item.capabilities.editIcon ? <div className="shrink-0 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
              {/* The picker is a control of this row like the rest, so it takes the same compact step
                  through the shared size axis instead of a box of its own (it used to be 28px). */}
              <ConversationIconPicker session={{ ...item, unread: item.unread === true }} disabled={disabled} size="icon-sm" />
            </div> : <div aria-hidden data-conversation-icon-slot className="hidden size-8 shrink-0 md:block" />}
          </ItemActions>
          {/* Model mark and owner face share one fixed-width track and the stack geometry, so both are the same
              size on one centre line and line up from row to row; after the optional controls of a compact row, so a
              phone keeps it on one edge. The mark's box is pinned to the stack size because MarkStrip's overlap margin would
              otherwise shrink a lone mark and pull the face in. */}
          <div className="flex shrink-0 items-center gap-2 self-center md:col-start-4 md:row-span-2 md:row-start-1">
            {item.unread === true ? <span data-conversation-badge className="hidden shrink-0 md:inline-flex lg:hidden xl:inline-flex"><StatusMark icon={Mail} tone="accent" label={t.conversationList.newReply} /></span> : null}
            {props.runningAgents && (props.runningAgents.count > 0 || props.runningAgents.truncated) ? <Tooltip open={agentsTip.open} onOpenChange={agentsTip.onOpenChange}>
              <TooltipAnchor asChild>
                <span data-conversation-badge role="img" aria-label={agentsLabel} tabIndex={0}
                  aria-describedby={agentsTip.open ? agentsTip.tooltipId : undefined}
                  className="hidden shrink-0 items-center rounded-md text-small tabular-nums text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex lg:hidden xl:inline-flex"
                  {...agentsTip.hover} {...agentsTip.focus} onClick={(event) => { event.stopPropagation(); agentsTip.show(); }}>
                  <span className="inline-flex size-5.5 items-center justify-center"><Users size={14} aria-hidden /></span>{props.runningAgents.count}{props.runningAgents.truncated ? '+' : ''}
                </span>
              </TooltipAnchor>
              <TooltipContent id={agentsTip.tooltipId} side="top" className="w-auto max-w-[min(--spacing(80),calc(100vw-var(--spacing)*6))] break-all px-2 py-1">{agentsLabel}</TooltipContent>
            </Tooltip> : null}
            {props.scheduledCount !== undefined && props.scheduledCount > 0 ? <Tooltip open={scheduledTip.open} onOpenChange={scheduledTip.onOpenChange}>
              <TooltipAnchor asChild>
                <span data-conversation-badge role="img" aria-label={scheduledLabel} tabIndex={0}
                  aria-describedby={scheduledTip.open ? scheduledTip.tooltipId : undefined}
                  className="hidden shrink-0 items-center rounded-md text-small tabular-nums text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex lg:hidden xl:inline-flex"
                  {...scheduledTip.hover} {...scheduledTip.focus} onClick={(event) => { event.stopPropagation(); scheduledTip.show(); }}>
                  <span className="inline-flex size-5.5 items-center justify-center"><Clock size={14} aria-hidden /></span>{props.scheduledCount}
                </span>
              </TooltipAnchor>
              <TooltipContent id={scheduledTip.tooltipId} side="top" className="w-auto max-w-[min(--spacing(80),calc(100vw-var(--spacing)*6))] break-all px-2 py-1">{scheduledLabel}</TooltipContent>
            </Tooltip> : null}
            {/* The pin is an optional mark of the same geometry, left of the fixed model/owner track so that
                track keeps its right edge. */}
            {item.pinnedAt ? <span data-conversation-pin className="inline-flex w-5.5 shrink-0">
              <MarkStrip kind="conversation-pin" marks={[{
                node: <Pin size={12} aria-hidden className="text-muted-foreground" />, label: t.conversationList.groups.pinned,
              }]} />
            </span> : null}
            <div data-conversation-faces className={`shrink-0 items-center gap-2 ${members || item.capabilities.invite || item.capabilities.leave ? 'flex' : props.view === 'all' ? 'flex w-5.5 lg:w-13' : 'hidden w-5.5 lg:flex'}`}>
              <span data-conversation-model className="hidden w-5.5 shrink-0 lg:inline-flex">
                <MarkStrip kind="conversation-model" marks={[{
                  node: <ModelIcon name={item.model} size={16} />,
                  label: brainModelQualifiedLabel({ provider: item.provider ?? '', model: item.model }),
                }]} />
              </span>
              {(item.capabilities.invite || item.capabilities.leave) ? <Popover open={membersOpen} onOpenChange={setMembersOpen}>
                <PopoverTrigger asChild><Button type="button" variant="ghost" size="sm" disabled={disabled}
                  aria-label={`${t.chat.participants}: ${title}`}>
                  <AvatarStack users={(members ?? [item.owner]).map(profileAvatarUser)} max={ROW_FACES} />
                </Button></PopoverTrigger>
                <PopoverContent align="end" className="w-[calc(100vw-var(--spacing)*6)] md:w-80">
                  {membersOpen ? <ConversationMembersPopover sessionId={item.id} canInvite={item.capabilities.invite} onClose={() => setMembersOpen(false)} onLeave={item.capabilities.leave ? () => { setMembersOpen(false); actions.leave(item); } : undefined} /> : null}
                </PopoverContent>
              </Popover> : members ? <AvatarStack users={members.map(profileAvatarUser)} max={ROW_FACES} />
                : ownerFace ? <span className={`inline-flex ${AVATAR_STACK_RING}`}><Avatar user={profileAvatarUser(ownerFace)} size={AVATAR_STACK_SIZE} /></span> : null}
            </div>
          </div>
          {/* Compact rows end on the time in a fixed-width cell, after the controls a row may or may not have,
              so the time and the faces before it hold one right edge in every row. */}
          <time dateTime={item.updated_at} title={time.title}
            className="w-20 shrink-0 truncate text-right text-small tabular-nums text-muted-foreground md:hidden">{time.label}</time>
        </div>
        <div data-conversation-control="more" className="col-start-3 row-start-1 shrink-0 justify-self-end md:col-start-7 md:row-span-2">
          <ActionMenu variant="kebab" size="sm" label={`${title}: ${t.chat.moreActions}`} items={menuItems} />
        </div>
      </Item>
    </ContextMenuTrigger>
    {menu ? <ContextMenu state={menu} onClose={() => setMenu(null)} label={`${title}: ${t.chat.moreActions}`} /> : null}
  </>;
}
